import { Injectable, inject } from '@angular/core';
import { Query } from '@datorama/akita';
import { OrderEditStore, OrderEditState } from './order-edit.store';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { OutboundOrder } from 'src/app/core/models/order/outboundOrder';

/**
 * Order Edit Query
 *
 * Provides observables and selectors for order edit state
 */
@Injectable({ providedIn: 'root' })
export class OrderEditQuery extends Query<OrderEditState> {
  protected override store: OrderEditStore;

  constructor() {
    const store = inject(OrderEditStore);

    super(store);

    this.store = store;
  }

  // ===== OBSERVABLES =====

  /**
   * Get initial loaded order
   */
  initialLoadedOrder$ = this.select((state) => state.initialLoadedOrder);

  /**
   * Get remote order baseline
   */
  remoteOrderBaseline$ = this.select((state) => state.remoteOrderBaseline);

  /**
   * Get remote order
   */
  remoteOrder$ = this.select((state) => state.remoteOrder);

  /**
   * Get current order (working copy)
   */
  currentOrder$ = this.select((state) => state.currentOrder);

  /**
   * Get current supplier
   */
  currentSupplier$ = this.select((state) => state.currentSupplier);

  /**
   * Get favourite articles catalog
   */
  favouriteArticlesCatalog$ = this.select((state) => state.favouriteArticlesCatalog);

  /**
   * Get catalog
   */
  catalog$ = this.select((state) => state.catalog);

  /**
   * Get categories
   */
  categories$ = this.select((state) => state.categories);

  /**
   * Get resolver articles
   */
  resolverArticles$ = this.select((state) => state.resolverArticles);

  /**
   * Get catalog source
   */
  catalogSource$ = this.select((state) => state.catalogSource);

  /**
   * Get contract
   */
  contract$ = this.select((state) => state.contract);

  /**
   * Get Typesense collection name
   */
  typesenseCollection$ = this.select((state) => state.typesenseCollection);

  /**
   * Get saving state
   */
  isSaving$ = this.select((state) => state.isSaving);

  /**
   * Get unsaved changes flag
   */
  unsavedChanges$ = this.select((state) => state.unsavedChanges);

  /**
   * Get total order value
   */
  totalOrderValue$: Observable<number> = this.currentOrder$.pipe(
    map((order) => {
      if (!order) return 0;
      return order.articles.reduce((sum, article) => {
        const price = article.customSalesPrice || article.standardSalesPrice || 0;
        const quantity = article.orderedQuantity || 0;
        return sum + price * quantity;
      }, 0);
    })
  );

  /**
   * Get article count (with quantities > 0)
   */
  articleCount$: Observable<number> = this.currentOrder$.pipe(
    map((order) => {
      if (!order) return 0;
      return order.articles.filter((a) => (a.orderedQuantity || 0) > 0).length;
    })
  );

  // ===== SYNCHRONOUS GETTERS =====

  /**
   * Get initial loaded order (sync)
   */
  getInitialLoadedOrder(): OutboundOrder | null {
    return this.getValue().initialLoadedOrder;
  }

  /**
   * Get remote order baseline (sync)
   */
  getRemoteOrderBaseline(): OutboundOrder | null {
    return this.getValue().remoteOrderBaseline;
  }

  /**
   * Get remote order (sync)
   */
  getRemoteOrder(): OutboundOrder | null {
    return this.getValue().remoteOrder;
  }

  /**
   * Get current order (sync)
   */
  getCurrentOrder(): OutboundOrder | null {
    return this.getValue().currentOrder;
  }

  /**
   * Get current supplier (sync)
   */
  getCurrentSupplier(): any {
    return this.getValue().currentSupplier;
  }

  /**
   * Get favourite articles catalog (sync)
   */
  getFavouriteArticlesCatalog(): any {
    return this.getValue().favouriteArticlesCatalog;
  }

  /**
   * Get catalog (sync)
   */
  getCatalog(): any {
    return this.getValue().catalog;
  }

  /**
   * Get categories (sync)
   */
  getCategories(): any[] {
    return this.getValue().categories;
  }

  /**
   * Get resolver articles (sync)
   */
  getResolverArticles(): any[] {
    return this.getValue().resolverArticles;
  }

  /**
   * Check if has unsaved changes
   */
  hasUnsavedChanges(): boolean {
    return this.getValue().unsavedChanges;
  }

  /**
   * Get contract (sync)
   */
  getContract(): any {
    return this.getValue().contract;
  }

  /**
   * Get Typesense collection (sync)
   */
  getTypesenseCollection(): string | null {
    return this.getValue().typesenseCollection;
  }

  /**
   * Check if currently saving
   */
  isSaving(): boolean {
    return this.getValue().isSaving;
  }
}
